<template>
  <div class="SiteAppUserFeedback narrow-container mx-4 pb-8">
    <div v-if="loading" class="spinner mt-12" align="center">
      <FlamePreloader :compact="true" :size="100" />
    </div>

    <template v-else-if="!isEnvironmentSite">
      <v-alert type="info" class="mt-6" dark>
        User Feedback is only available on
        <strong>staging</strong> and <strong>development</strong> sites. Switch
        site mode from the toolbar to manage feedback.
      </v-alert>
      <div class="d-flex flex-wrap gap-2 mt-4">
        <v-btn
          color="y1"
          dark
          @click="switchSiteMode(SITE_MODE_STAGING)"
          :loading="switchingMode"
        >
          Switch to Staging
        </v-btn>
        <v-btn
          outlined
          dark
          @click="switchSiteMode(SITE_MODE_DEVELOPMENT)"
          :loading="switchingMode"
        >
          Switch to Development
        </v-btn>
      </div>
    </template>

    <template v-else>
      <v-alert
        v-if="redeployInProgress"
        type="warning"
        class="mt-4"
        dark
        dense
      >
        Redeploy in progress — the feedback widget will update when the deploy
        finishes.
      </v-alert>

      <v-alert
        v-if="!appSetting"
        type="info"
        class="mt-4"
        dark
        dense
      >
        Install User Feedback from the
        <router-link :to="{ name: 'site_apps' }" class="SiteAppUserFeedback__page-link">
          apps list
        </router-link>
        on this environment site first.
      </v-alert>

      <v-card v-else dark class="SiteAppUserFeedback__settings mt-4 pa-4">
        <div class="d-flex flex-wrap align-center justify-space-between">
          <div class="d-flex align-center">
            <v-switch
              v-model="isEnabled"
              :label="isEnabled ? 'Feedback enabled' : 'Feedback disabled'"
              color="y1"
              hide-details
              class="mt-0"
              :disabled="toggleDisabled"
              @change="toggleApp"
            />
          </div>
          <div class="d-flex align-center flex-wrap gap-2">
            <v-btn
              v-if="previewSiteHref"
              text
              color="y1"
              :href="previewSiteHref"
              target="_blank"
              rel="noopener"
            >
              <v-icon left small>mdi-open-in-new</v-icon>
              Open preview
            </v-btn>
          </div>
        </div>
        <p class="text-caption text--secondary mb-0 mt-2">
          Reviewers submit feedback from the widget on your deployed preview
          site. Toggling enabled triggers a redeploy.
        </p>
      </v-card>

      <div class="mt-8">
        <div class="d-flex flex-wrap align-center justify-space-between mb-4">
          <h3 class="text-h6 white--text mb-0">Feedback inbox</h3>
          <v-btn-toggle
            v-model="statusFilter"
            mandatory
            dense
            class="SiteAppUserFeedback__status-filter"
          >
            <v-btn value="" small>All</v-btn>
            <v-btn value="open" small>Open</v-btn>
            <v-btn value="resolved" small>Resolved</v-btn>
            <v-btn value="dismissed" small>Dismissed</v-btn>
          </v-btn-toggle>
        </div>

        <div v-if="feedbacksLoading" class="py-8 text-center">
          <FlamePreloader :compact="true" :size="60" />
        </div>

        <v-card
          v-else-if="feedbacks.length === 0"
          dark
          class="pa-8 text-center SiteAppUserFeedback__empty"
        >
          <v-icon size="48" color="n3" class="mb-4">mdi-comment-text-outline</v-icon>
          <p class="mb-0 white--text">
            No feedback yet
            <span v-if="statusFilter"> with status “{{ statusFilter }}”</span>.
          </p>
          <p
            v-if="isEnabled"
            class="text-caption text--secondary mt-2 mb-0"
          >
            Use the widget on your preview site to collect comments.
          </p>
        </v-card>

        <v-data-table
          v-else
          :headers="headers"
          :items="feedbacks"
          :items-per-page="perPage"
          :page.sync="page"
          :server-items-length="totalCount"
          :footer-props="{
            'items-per-page-options': [25, 50],
          }"
          class="SiteAppUserFeedback__table"
          dark
          @update:items-per-page="onPerPageChange"
          @click:row="openDetail"
        >
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="statusColor(item.status)"
              text-color="white"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.page_url="{ item }">
            <a
              :href="item.page_url"
              target="_blank"
              rel="noopener"
              class="SiteAppUserFeedback__page-link"
              :title="item.page_url"
              @click.stop
            >
              {{ truncate(formatPagePath(item.page_url), 48) }}
            </a>
          </template>

          <template v-slot:item.target="{ item }">
            <div
              class="SiteAppUserFeedback__target-cell"
              :title="targetTooltip(item)"
            >
              <div class="SiteAppUserFeedback__target-label">
                {{ truncate(targetLabel(item), 40) }}
              </div>
              <code
                v-if="showSelectorSubline(item)"
                class="SiteAppUserFeedback__target-selector"
              >
                {{ truncate(item.selector, 48) }}
              </code>
            </div>
          </template>

          <template v-slot:item.comment="{ item }">
            {{ truncate(item.comment, 60) }}
          </template>

          <template v-slot:item.author="{ item }">
            <span v-if="item.author_name || item.author_email">
              {{ item.author_name || 'Anonymous'
              }}<span v-if="item.author_email" class="text--secondary">
                &lt;{{ item.author_email }}&gt;</span
              >
            </span>
            <span v-else class="text--secondary">Anonymous</span>
          </template>

          <template v-slot:item.device="{ item }">
            {{ formatDevice(item) }}
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense dark>
                <v-list-item
                  v-if="item.status !== 'resolved'"
                  @click="updateStatus(item, 'resolved')"
                >
                  <v-list-item-title>Mark resolved</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="item.status !== 'dismissed'"
                  @click="updateStatus(item, 'dismissed')"
                >
                  <v-list-item-title>Dismiss</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="item.status !== 'open'"
                  @click="updateStatus(item, 'open')"
                >
                  <v-list-item-title>Reopen</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="confirmDelete(item)">
                  <v-list-item-title class="red--text"
                    >Delete</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </div>
    </template>

    <v-navigation-drawer
      v-model="detailOpen"
      app
      right
      temporary
      width="480"
      class="SiteAppUserFeedback__drawer"
    >
      <div v-if="selectedFeedback" class="SiteAppUserFeedback__drawer-inner">
        <div class="SiteAppUserFeedback__drawer-body pa-4">
          <div class="d-flex align-center justify-space-between mb-4">
            <h3 class="text-h6 white--text mb-0">Feedback detail</h3>
            <v-btn icon dark @click="detailOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-chip
            small
            :color="statusColor(selectedFeedback.status)"
            text-color="white"
            class="mb-4"
          >
            {{ selectedFeedback.status }}
          </v-chip>

          <div v-if="selectedFeedback.screenshot_url" class="mb-4">
            <a
              :href="selectedFeedback.screenshot_url"
              target="_blank"
              rel="noopener"
            >
              <v-img
                :src="selectedFeedback.screenshot_url"
                max-height="200"
                contain
                class="rounded"
              />
            </a>
          </div>

          <dl class="SiteAppUserFeedback__detail-list">
            <dt>Comment</dt>
            <dd>{{ selectedFeedback.comment }}</dd>

            <dt>Page</dt>
            <dd>
              <a
                :href="selectedFeedback.page_url"
                target="_blank"
                rel="noopener"
                class="SiteAppUserFeedback__page-link"
              >
                {{ selectedFeedback.page_title || selectedFeedback.page_url }}
              </a>
            </dd>

            <dt v-if="selectedFeedback.feedback_type">Type</dt>
            <dd v-if="selectedFeedback.feedback_type">
              {{ selectedFeedback.feedback_type }}
            </dd>

            <dt v-if="selectedFeedback.element_summary">Element</dt>
            <dd v-if="selectedFeedback.element_summary">
              {{ selectedFeedback.element_summary }}
            </dd>

            <dt v-if="selectedFeedback.selector">Selector</dt>
            <dd v-if="selectedFeedback.selector">
              <code class="SiteAppUserFeedback__selector-code">{{
                selectedFeedback.selector
              }}</code>
            </dd>

            <dt v-if="selectedFeedback.selected_text">Selected text</dt>
            <dd v-if="selectedFeedback.selected_text">
              {{ selectedFeedback.selected_text }}
            </dd>

            <dt
              v-if="
                selectedFeedback.author_name || selectedFeedback.author_email
              "
            >
              Author
            </dt>
            <dd
              v-if="
                selectedFeedback.author_name || selectedFeedback.author_email
              "
            >
              {{ selectedFeedback.author_name }}
              <span v-if="selectedFeedback.author_email">
                ({{ selectedFeedback.author_email }})
              </span>
            </dd>

            <dt>Device</dt>
            <dd>{{ formatDevice(selectedFeedback) }}</dd>

            <dt>Viewport</dt>
            <dd>
              {{ selectedFeedback.viewport_width }}×{{
                selectedFeedback.viewport_height
              }}
            </dd>

            <dt>Submitted</dt>
            <dd>{{ formatDate(selectedFeedback.created_at) }}</dd>
          </dl>
        </div>

        <div class="SiteAppUserFeedback__drawer-footer pa-4">
          <div
            v-if="canNavigateDetail"
            class="SiteAppUserFeedback__drawer-nav mb-4"
          >
            <v-btn
              text
              dark
              small
              :disabled="!canGoPrevious"
              @click="goToPrevious"
            >
              <v-icon left small>mdi-chevron-left</v-icon>
              Previous
            </v-btn>
            <span class="SiteAppUserFeedback__drawer-nav-count text-caption">
              {{ selectedFeedbackIndex + 1 }} of {{ feedbacks.length }}
            </span>
            <v-btn text dark small :disabled="!canGoNext" @click="goToNext">
              Next
              <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <div class="SiteAppUserFeedback__drawer-actions">
            <div class="SiteAppUserFeedback__drawer-actions-primary">
              <v-btn
                v-if="selectedFeedback.status !== 'resolved'"
                color="y1"
                dark
                small
                class="mr-2 mb-2"
                @click="updateStatus(selectedFeedback, 'resolved')"
              >
                Resolve
              </v-btn>
              <v-btn
                v-if="selectedFeedback.status !== 'dismissed'"
                outlined
                dark
                small
                class="mr-2 mb-2"
                @click="updateStatus(selectedFeedback, 'dismissed')"
              >
                Dismiss
              </v-btn>
              <v-btn
                v-if="selectedFeedback.status !== 'open'"
                outlined
                dark
                small
                class="mr-2 mb-2"
                @click="updateStatus(selectedFeedback, 'open')"
              >
                Reopen
              </v-btn>
            </div>
            <v-btn
              text
              color="red"
              small
              class="mb-2 SiteAppUserFeedback__drawer-delete"
              @click="confirmDelete(selectedFeedback)"
            >
              Delete
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import FlamePreloader from '@/components/_Common/FlamePreloader'
import { MODAL_ALERT } from '@/components/Modals'
import { SiteFeedback } from '@/resources/forge'
import { isUserFeedbackForgeApp } from '@/constants/forgeApps'
import {
  SITE_MODE_DEVELOPMENT,
  SITE_MODE_STAGING,
} from '@/models/Site'

export default {
  name: 'SiteAppUserFeedback',
  components: { FlamePreloader },
  data() {
    return {
      SITE_MODE_STAGING,
      SITE_MODE_DEVELOPMENT,
      loading: true,
      feedbacksLoading: false,
      switchingMode: false,
      isEnabled: false,
      toggling: false,
      appSetting: null,
      feedbacks: [],
      statusFilter: '',
      page: 1,
      perPage: 25,
      totalCount: 0,
      detailOpen: false,
      selectedFeedback: null,
      headers: [
        { text: 'Status', value: 'status', width: '100px' },
        { text: 'Page', value: 'page_url' },
        { text: 'Target', value: 'target', sortable: false },
        { text: 'Comment', value: 'comment' },
        { text: 'Author', value: 'author', sortable: false },
        { text: 'Device', value: 'device', sortable: false },
        { text: 'Created', value: 'created_at' },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
          width: '48px',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      activeSite: (state) => state.sites.active,
    }),
    isEnvironmentSite() {
      return (
        this.activeSite?.mode === SITE_MODE_STAGING ||
        this.activeSite?.mode === SITE_MODE_DEVELOPMENT
      )
    },
    previewSiteHref() {
      const url = this.activeSite?.url
      if (!url) return null
      return url.startsWith('http') ? url : `https://${url}`
    },
    redeployInProgress() {
      return this.activeSite?.currentVersionStatus === 'deploying'
    },
    toggleDisabled() {
      return this.toggling || this.redeployInProgress || !this.appSetting
    },
    selectedFeedbackIndex() {
      if (!this.selectedFeedback) return -1
      return this.feedbacks.findIndex((f) => f.id === this.selectedFeedback.id)
    },
    canNavigateDetail() {
      return this.feedbacks.length > 1 && this.selectedFeedbackIndex !== -1
    },
    canGoPrevious() {
      return this.canNavigateDetail && this.selectedFeedbackIndex > 0
    },
    canGoNext() {
      return (
        this.canNavigateDetail &&
        this.selectedFeedbackIndex < this.feedbacks.length - 1
      )
    },
  },
  watch: {
    statusFilter() {
      this.page = 1
      this.fetchFeedbacks()
    },
    page() {
      this.fetchFeedbacks()
    },
    perPage() {
      this.page = 1
      this.fetchFeedbacks()
    },
    'activeSite.id'() {
      this.init()
    },
    redeployInProgress(isDeploying, wasDeploying) {
      if (wasDeploying && !isDeploying) {
        this.fetchFeedbacks()
      }
    },
  },
  async mounted() {
    await this.init()
    window.addEventListener('keydown', this.onDetailKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onDetailKeydown)
  },
  methods: {
    async init() {
      this.loading = true
      try {
        if (this.isEnvironmentSite) {
          await this.$store.dispatch(
            'forgeApps/fetchInstalledApps',
            this.activeSite.id
          )
          const installed = this.$store.state.forgeApps?.installedApps || []
          this.appSetting =
            installed.find((s) => isUserFeedbackForgeApp(s)) || null
          this.isEnabled = this.appSetting?.enabled || false
          await this.fetchFeedbacks()
        }
      } catch (error) {
        console.error('SiteAppUserFeedback init error:', error)
        this.$root.$emit('showSnack', {
          message: 'Failed to load User Feedback',
          color: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    onPerPageChange(value) {
      this.perPage = value
    },
    async fetchFeedbacks() {
      if (!this.isEnvironmentSite || !this.activeSite?.id) return
      this.feedbacksLoading = true
      try {
        const params = {
          site_id: this.activeSite.id,
          page: this.page,
          per_page: this.perPage,
        }
        if (this.statusFilter) {
          params.status = this.statusFilter
        }
        const response = await SiteFeedback.list(params)
        this.feedbacks = response.body.site_feedbacks || []
        this.totalCount = response.body.meta?.total ?? this.feedbacks.length
      } catch (error) {
        console.error('fetchFeedbacks error:', error)
        this.$root.$emit('showSnack', {
          message: 'Failed to load feedback',
          color: 'error',
        })
      } finally {
        this.feedbacksLoading = false
      }
    },
    async toggleApp() {
      if (!this.appSetting?.forge_app_id || !this.activeSite?.id) return
      this.toggling = true
      try {
        await this.$store.dispatch('forgeApps/updateAppSetting', {
          siteId: this.activeSite.id,
          appId: this.appSetting.forge_app_id,
          enabled: this.isEnabled,
          config: this.appSetting.config || {},
        })
        this.$root.$emit('showSnack', {
          message: `Feedback ${this.isEnabled ? 'enabled' : 'disabled'}`,
          color: 'success',
        })
      } catch (error) {
        this.isEnabled = !this.isEnabled
        this.$root.$emit('showSnack', {
          message: 'Failed to update feedback setting',
          color: 'error',
        })
      } finally {
        this.toggling = false
      }
    },
    async updateStatus(item, status) {
      try {
        await SiteFeedback.update(
          { id: item.id },
          { site_feedback: { status } }
        )
        const index = this.feedbacks.findIndex((f) => f.id === item.id)
        if (index !== -1) {
          const updated = { ...this.feedbacks[index], status }
          this.$set(this.feedbacks, index, updated)
        }
        if (this.selectedFeedback?.id === item.id) {
          this.selectedFeedback = { ...this.selectedFeedback, status }
        }
        this.$root.$emit('showSnack', {
          message: `Marked as ${status}`,
          color: 'success',
        })
        if (
          this.statusFilter &&
          this.statusFilter !== status &&
          index !== -1
        ) {
          this.feedbacks.splice(index, 1)
          this.totalCount = Math.max(0, this.totalCount - 1)
        }
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to update status',
          color: 'error',
        })
      }
    },
    confirmDelete(item) {
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Delete feedback',
          text: 'Remove this feedback permanently?',
          actionBtnLabel: 'Delete',
          showCancelBtn: true,
          action: () => this.deleteFeedback(item),
        },
      })
    },
    async deleteFeedback(item) {
      try {
        await SiteFeedback.delete({ id: item.id })
        const deletedIndex = this.feedbacks.findIndex((f) => f.id === item.id)
        this.feedbacks = this.feedbacks.filter((f) => f.id !== item.id)
        this.totalCount = Math.max(0, this.totalCount - 1)
        if (this.selectedFeedback?.id === item.id) {
          const nextItem =
            this.feedbacks[deletedIndex] ||
            this.feedbacks[Math.max(0, deletedIndex - 1)]
          if (nextItem) {
            this.selectedFeedback = nextItem
          } else {
            this.detailOpen = false
            this.selectedFeedback = null
          }
        }
        this.$root.$emit('showSnack', {
          message: 'Feedback deleted',
          color: 'success',
        })
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to delete feedback',
          color: 'error',
        })
      }
    },
    openDetail(item) {
      this.selectedFeedback = item
      this.detailOpen = true
    },
    goToPrevious() {
      if (!this.canGoPrevious) return
      this.selectedFeedback = this.feedbacks[this.selectedFeedbackIndex - 1]
    },
    goToNext() {
      if (!this.canGoNext) return
      this.selectedFeedback = this.feedbacks[this.selectedFeedbackIndex + 1]
    },
    onDetailKeydown(event) {
      if (!this.detailOpen) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.goToNext()
      }
    },
    async switchSiteMode(mode) {
      const parentId =
        this.activeSite.mode === 'production'
          ? this.activeSite.id
          : this.activeSite.parent_site_id
      if (!parentId) return
      this.switchingMode = true
      try {
        await this.$store.dispatch('sites/getSiteMode', {
          id: parentId,
          mode,
        })
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to switch site mode',
          color: 'error',
        })
      } finally {
        this.switchingMode = false
      }
    },
    statusColor(status) {
      const colors = {
        open: 'green darken-1',
        resolved: 'teal darken-2',
        dismissed: 'grey darken-1',
      }
      return colors[status] || 'grey'
    },
    formatDevice(item) {
      const parts = [
        item.device_type,
        item.browser_name,
        item.os_name,
      ].filter(Boolean)
      return parts.join(' · ') || '—'
    },
    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString()
    },
    truncate(str, max) {
      if (!str) return '—'
      return str.length > max ? `${str.slice(0, max)}…` : str
    },
    formatPagePath(url) {
      if (!url) return '—'
      try {
        const { pathname, search, hash } = new URL(url)
        const path = `${pathname || '/'}${search}${hash}`
        return path
      } catch {
        return url.startsWith('/') ? url : url
      }
    },
    targetLabel(item) {
      if (item.feedback_type === 'text' && item.selected_text) {
        return item.selected_text
      }
      return item.element_summary || item.selector || '—'
    },
    targetTooltip(item) {
      const parts = []
      const label = this.targetLabel(item)
      if (label && label !== '—') parts.push(label)
      if (this.showSelectorSubline(item)) {
        parts.push(item.selector)
      }
      return parts.join('\n') || ''
    },
    showSelectorSubline(item) {
      if (!item.selector) return false
      if (item.feedback_type === 'text' && item.selected_text) return true
      return !!item.element_summary
    },
  },
}
</script>

<style lang="scss" scoped>
$style: SiteAppUserFeedback;

.#{$style} {
  &__settings {
    background: $N7 !important;
    border: 1px solid $N5;
  }

  &__empty {
    background: $N7 !important;
    border: 1px solid $N5;
  }

  &__table {
    background: transparent !important;
  }

  &__page-link {
    color: $Y1;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  &__target-cell {
    max-width: 220px;
  }

  &__target-label {
    color: $white;
    line-height: 1.3;
  }

  &__target-selector,
  &__selector-code {
    display: block;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 11px;
    color: $N2;
    word-break: break-all;
    margin-top: 4px;
    background: transparent;
  }

  &__target-selector {
    line-height: 1.25;
  }

  &__selector-code {
    padding: 0;
  }

  &__detail-list {
    dt {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $N2;
      margin-top: 12px;
    }
    dd {
      margin: 4px 0 0;
      color: $white;
      word-break: break-word;
    }
  }

  &__status-filter {
    background: $B5 !important;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid $B4;

    ::v-deep .v-btn {
      height: 32px !important;
      min-width: 72px !important;
      background-color: $B5 !important;
      color: $B2 !important;
      border: none !important;
      letter-spacing: normal;
      text-transform: none;

      &.v-item--active {
        background-color: $B1 !important;
        color: $white !important;
      }
    }
  }

  &__drawer.theme--dark {
    background: $N7 !important;
  }

  &__drawer-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  &__drawer-footer {
    flex-shrink: 0;
    border-top: 1px solid $N5;
    background: $N7;
  }

  &__drawer-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__drawer-nav-count {
    color: $N2;
    flex-shrink: 0;
  }

  &__drawer-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px 16px;
  }

  &__drawer-actions-primary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__drawer-delete {
    flex-shrink: 0;
    margin-left: auto;
  }
}
</style>
